import React from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Box from '@mui/material/Box';
import visuallyHidden from '@mui/utils/visuallyHidden';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import useTableSettings from '../hooks/useTableSettings';

// Helper function to compare values for sorting
function descendingComparator(a, b, orderBy) {
  // Handle null/undefined values
  if (b[orderBy] === null || b[orderBy] === undefined) return -1;
  if (a[orderBy] === null || a[orderBy] === undefined) return 1;

  // Check if values are dates
  if (isDate(a[orderBy]) && isDate(b[orderBy])) {
    return new Date(b[orderBy]) - new Date(a[orderBy]);
  }

  // Check if values are numbers as strings (like "$123.45")
  const aNumMatch = String(a[orderBy]).match(/[+-]?\d+(\.\d+)?/g);
  const bNumMatch = String(b[orderBy]).match(/[+-]?\d+(\.\d+)?/g);
  
  if (aNumMatch && bNumMatch) {
    return parseFloat(bNumMatch[0]) - parseFloat(aNumMatch[0]);
  }

  // Default string comparison
  return b[orderBy].toString().localeCompare(a[orderBy].toString());
}

function isDate(value) {
  if (!value) return false;
  const parsed = new Date(value);
  return !isNaN(parsed) && parsed.toString() !== 'Invalid Date';
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function SortableTableHead(props) {
  const { order, orderBy, onRequestSort, headCells, tableCellProps = {} } = props;
  
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align || 'left'}
            padding={headCell.padding || 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={headCell.sx || {}}
            {...tableCellProps}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              disabled={headCell.disableSorting}
              aria-label={`Sort by ${headCell.label} ${orderBy === headCell.id ? (order === 'asc' ? 'descending' : 'ascending') : ''}`}
              tabIndex={0}
              onKeyDown={(e) => {
                // Handle keyboard activation with Enter or Space
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  createSortHandler(headCell.id)(e);
                }
              }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

SortableTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  headCells: PropTypes.array.isRequired,
  tableCellProps: PropTypes.object,
};

export default function SortableTable(props) {
  const {
    rows = [],
    headCells,
    defaultOrderBy = '',
    defaultOrder = 'asc',
    rowComponent = TableRow,
    tableProps = {},
    tableContainerProps = {},
    tableCellProps = {},
    renderRow,
    emptyContent,
    paginationEnabled = true,
    defaultRowsPerPage = 25,
    tableId = 'default-table',
  } = props;

  // Use our custom hook to persist settings in session storage
  const {
    page, 
    rowsPerPage, 
    order, 
    orderBy, 
    setPage, 
    setRowsPerPage, 
    setSortOrder
  } = useTableSettings(tableId, {
    orderBy: defaultOrderBy || (headCells[0]?.id || ''),
    order: defaultOrder,
    rowsPerPage: defaultRowsPerPage
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setSortOrder(property, newOrder);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  // Apply sorting to the rows
  const sortedRows = stableSort(rows, getComparator(order, orderBy));
  
  // Apply pagination if enabled
  const paginatedRows = paginationEnabled
    ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedRows;

  // Get the accessible sort status for screen readers
  const getAriaLabel = () => {
    if (!orderBy) return 'Table with sortable columns';
    const columnLabel = headCells.find(cell => cell.id === orderBy)?.label || orderBy;
    return `Table sorted by ${columnLabel} in ${order === 'asc' ? 'ascending' : 'descending'} order`;
  };

  return (
    <Box>
      <TableContainer 
        component={Paper} 
        {...tableContainerProps}
        role="region"
        aria-label="Sortable table"
      >
        <Table 
          aria-label={getAriaLabel()}
          {...tableProps}
        >
          <SortableTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            headCells={headCells}
            tableCellProps={tableCellProps}
          />
          <TableBody>
            {paginatedRows.length > 0 ? (
              paginatedRows.map((row, index) => renderRow(row, index))
            ) : (
              emptyContent && (
                <TableRow>
                  <TableCell colSpan={headCells.length} align="center">
                    {emptyContent}
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {paginationEnabled && rows.length > 0 && (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ display: 'flex', justifyContent: 'flex-end' }}
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} of ${count}`
          }
        />
      )}
    </Box>
  );
}

SortableTable.propTypes = {
  rows: PropTypes.array.isRequired,
  headCells: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      align: PropTypes.string,
      padding: PropTypes.string,
      disableSorting: PropTypes.bool,
      sx: PropTypes.object,
    })
  ).isRequired,
  defaultOrderBy: PropTypes.string,
  defaultOrder: PropTypes.oneOf(['asc', 'desc']),
  tableProps: PropTypes.object,
  tableContainerProps: PropTypes.object,
  tableCellProps: PropTypes.object,
  renderRow: PropTypes.func.isRequired,
  emptyContent: PropTypes.node,
  paginationEnabled: PropTypes.bool,
  defaultRowsPerPage: PropTypes.number,
  tableId: PropTypes.string,
}; 